import { getContrastColor } from "@gettactic/helpers/src/colors/getContrastColor";
import {
  DEFAULT_FONT_FAMILY,
  DEFAULT_NODE_CIRCLE_RADIUS,
  DEFAULT_NODE_CIRCLE_STROKE_WIDTH,
  DEFAULT_NODE_RECT_COLOR
} from "@gettactic/map-builder/src/components/map/defines";
import {
  getInteractiveZoneStyles,
  getOrientationLayout
} from "@gettactic/map-builder/src/components/map/shapes/SpotShape/helpers";
import { InteractiveZoneSVG } from "@gettactic/map-builder/src/components/map/shapes/SpotShape/InteractiveZoneSVG";
import { SpotStateAreaSVG } from "@gettactic/map-builder/src/components/map/shapes/SpotShape/SpotStateAreaSVG";
import type {
  MapInteractiveZone,
  MapInteractiveZoneActionOptions,
  Shape,
  SpotShape
} from "@gettactic/map-builder/src/types";
import { getEffectiveSpotAppearance } from "@gettactic/map-builder/src/utils/spots";
import { computeStateIconCenterOffset } from "@gettactic/map-builder/src/utils/stateIconCentering";
import React from "react";
import { ShapeSvg } from "../ShapeSvg";
import { buildLocalShapeTransform } from "../shapeTransform";

export interface SpotSvgProps {
  shape: SpotShape;
  /** Colour inherited from an enclosing group. */
  parentGroupColor?: string;
  teamColors?: Record<string, string>;
  /** Host-supplied state for this resource (walk-up etc.). */
  interactiveZone?: MapInteractiveZone;
  /** Viewer: the spot is a click/hover target for the host. */
  isViewMode?: boolean;
  highlightResource?: (
    resourceId: string | null,
    options: MapInteractiveZoneActionOptions
  ) => void;
}

/**
 * Display colour priority, as in the editor's `useSpotColor`: manual colour,
 * enclosing group colour, the resource's team colour, then the default.
 */
export const resolveSpotColor = (
  shape: SpotShape,
  parentGroupColor?: string,
  teamColors?: Record<string, string>
): string => {
  if (shape.colorSource === "manual" && shape.color) return shape.color;
  if (!shape.colorSource && shape.color) return shape.color;
  if (parentGroupColor) return parentGroupColor;
  const teamColor = shape.resource_id ? teamColors?.[shape.resource_id] : null;
  return teamColor || DEFAULT_NODE_RECT_COLOR;
};

/** Rendering of a spot: body, label and the availability dot. The editor
 *  draws the dot in its resting style with the resource marker; the viewer
 *  draws the live state (avatar, state icon, highlight area) through the
 *  same components as `ViewSVG`, so both maps look alike. */
export const SpotSvg: React.FC<SpotSvgProps> = ({
  shape,
  parentGroupColor,
  teamColors,
  interactiveZone,
  isViewMode,
  highlightResource
}) => {
  // Same native interactivity as the SVG viewer's spot: the whole spot is
  // the click/hover target, reported in viewport coordinates.
  const isNotSelectable = !!interactiveZone?.isNotSelectable;
  const isInteractive =
    !!isViewMode &&
    !shape.isInactive &&
    !!shape.resource_id &&
    !isNotSelectable;
  const isClickable = isInteractive && !!interactiveZone?.onAction;
  const handleClick = (event: React.MouseEvent<SVGGElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const bounds = event.currentTarget.getBoundingClientRect();
    interactiveZone?.onAction?.({
      x: bounds.x,
      y: bounds.y,
      width: bounds.width,
      height: bounds.height,
      isCmdOrCtrl: event.metaKey || event.ctrlKey
    });
  };
  const [isSpotHovered, setIsSpotHovered] = React.useState(false);
  const handleMouseEnter = (event: React.MouseEvent<SVGGElement>) => {
    setIsSpotHovered(true);
    if (!shape.resource_id || !highlightResource) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    highlightResource(shape.resource_id, {
      x: bounds.x,
      y: bounds.y,
      width: bounds.width,
      height: bounds.height
    });
  };
  const handleMouseLeave = () => {
    setIsSpotHovered(false);
    if (shape.resource_id && highlightResource) {
      highlightResource(null, { x: 0, y: 0 });
    }
  };
  const appearance = getEffectiveSpotAppearance(
    shape.spotType,
    shape.spotAppearance
  );
  const layout = React.useMemo(
    () =>
      getOrientationLayout(
        appearance,
        shape.orientation,
        shape.labelPlacement,
        shape.labelOrientation || "auto",
        0,
        0,
        shape.spacing || 0,
        shape.rowSpacing || 0,
        shape.width,
        shape.height
      ),
    [
      appearance,
      shape.orientation,
      shape.labelPlacement,
      shape.labelOrientation,
      shape.spacing,
      shape.rowSpacing,
      shape.width,
      shape.height
    ]
  );
  const rectColor = resolveSpotColor(shape, parentGroupColor, teamColors);
  const labelTone = getContrastColor(rectColor);

  const isRoundRoom = appearance === "circle";
  const hasRect =
    appearance === "desk" ||
    appearance === "meeting_room" ||
    appearance === "parking_lot" ||
    appearance === "parking_space" ||
    isRoundRoom;
  let cornerRadius = shape.rectProps?.cornerRadius || 0;
  if (appearance === "meeting_room") cornerRadius = 15;
  else if (isRoundRoom) cornerRadius = 40;

  const hasOwnRect = !!(layout.rect.width && layout.rect.height);
  const dotOffset = hasOwnRect ? null : computeStateIconCenterOffset(shape);
  const dot = {
    x: layout.circle.x + (dotOffset?.offsetX ?? 0),
    y: layout.circle.y + (dotOffset?.offsetY ?? 0)
  };
  const circleRadius = shape.circleProps?.radius || DEFAULT_NODE_CIRCLE_RADIUS;
  const circleStrokeWidth =
    shape.circleProps?.strokeWidth || DEFAULT_NODE_CIRCLE_STROKE_WIDTH;
  const dotStyle = getInteractiveZoneStyles({
    isSelected: interactiveZone?.isSelected || false,
    isLastAccessed: interactiveZone?.isLastUsed || false,
    isOccupied: interactiveZone?.isOccupied || false,
    notAssignedResource: interactiveZone?.notAssigned || false,
    notAssignedUser: !interactiveZone?.assignedUser,
    requiresApproval: interactiveZone?.requiresApproval || false,
    isWalkUp: interactiveZone?.isWalkUp || false,
    hasSchedules: interactiveZone?.hasSchedules || false,
    showCircleProfilePhoto: interactiveZone?.showCircleProfilePhoto || false,
    hasCheckedIn: interactiveZone?.actualSchedule?.has_checked_in || false
  });

  const textWidth = (shape.textProps?.width ?? layout.text.width) || 0;
  const hasTextClip =
    appearance !== "meeting_room" && appearance !== "circle" && textWidth > 0;
  const fontSize = shape.fontSize || 16;
  const isLabelOutside =
    shape.labelPlacement === "outside" ||
    shape.labelPlacement === "outside-node";
  const textColor =
    shape.textColor ||
    shape.textProps?.color ||
    (isLabelOutside ? "black" : labelTone === "light" ? "white" : "black");
  const textAnchor =
    shape.textAlign === "center"
      ? "middle"
      : shape.textAlign === "right"
        ? "end"
        : "start";
  const marginLeft = shape.textProps?.marginLeft || 0;
  const textX =
    shape.textAlign === "center"
      ? layout.text.x + marginLeft + (layout.text.width || 0) / 2
      : shape.textAlign === "right"
        ? layout.text.x + marginLeft + (layout.text.width || 0)
        : layout.text.x + marginLeft;
  const textY =
    layout.text.y +
    (shape.textProps?.marginTop || 0) +
    fontSize / 2 -
    ((appearance === "meeting_room" || isRoundRoom) && fontSize > 26
      ? fontSize / 2
      : 0);
  const textRotation = shape.textProps?.rotation ?? layout.text.rotation;
  const textStyle: React.CSSProperties = {
    fontSize: `${fontSize}px`,
    fontFamily: shape.fontFamily || DEFAULT_FONT_FAMILY,
    fontWeight: shape.isBold ? "700" : "300",
    fontStyle: shape.isItalic ? "italic" : "normal",
    fill: textColor
  };

  return (
    <g
      transform={buildLocalShapeTransform(shape)}
      opacity={isNotSelectable ? 0.35 : undefined}
      onClick={isClickable ? handleClick : undefined}
      onMouseEnter={isInteractive ? handleMouseEnter : undefined}
      onMouseLeave={isInteractive ? handleMouseLeave : undefined}
      style={isInteractive ? { cursor: "pointer" } : undefined}
    >
      {shape.companions?.map((companion: Shape) => (
        <ShapeSvg key={companion.id} shape={companion} />
      ))}
      {isRoundRoom ? (
        <circle
          className="spot-body"
          cx={layout.rect.x + layout.rect.width / 2}
          cy={layout.rect.y + layout.rect.height / 2}
          r={layout.rect.width / 2}
          fill={rectColor}
          opacity={shape.rectProps?.opacity || 1}
          stroke="transparent"
          strokeWidth={0.5}
        />
      ) : hasRect ? (
        <rect
          className="spot-body"
          x={layout.rect.x}
          y={layout.rect.y}
          width={layout.rect.width}
          height={layout.rect.height}
          transform={`rotate(${layout.rect.rotation} ${layout.rect.x} ${layout.rect.y})`}
          rx={cornerRadius}
          fill={rectColor}
          opacity={shape.rectProps?.opacity || 1}
          stroke="transparent"
          strokeWidth={0.5}
        />
      ) : null}
      {isViewMode ? (
        <SpotStateAreaSVG
          shape={shape}
          layout={layout}
          interactiveZone={interactiveZone}
          hasRect={hasRect}
          cornerRadius={cornerRadius}
          isSpotHovered={isSpotHovered}
        />
      ) : (
        <>
          <SpotStateAreaSVG
            shape={shape}
            layout={layout}
            interactiveZone={interactiveZone}
            hasRect={hasRect}
            cornerRadius={cornerRadius}
            isEditorPreview
          />
          <StateAreaEditPreview
            shape={shape}
            layout={layout}
            hasRect={hasRect}
            cornerRadius={cornerRadius}
          />
        </>
      )}
      {shape.labelPlacement === "hidden" ? null : hasTextClip ? (
        <svg
          x={layout.text.x}
          y={layout.text.y - fontSize}
          width={textWidth}
          height={fontSize * 3}
          overflow="hidden"
        >
          <text
            x={textX - layout.text.x}
            y={fontSize + (shape.textProps?.marginTop || 0) + fontSize / 2}
            transform={`rotate(${textRotation} ${textX - layout.text.x} ${fontSize})`}
            style={textStyle}
            textAnchor={textAnchor}
            dominantBaseline="middle"
          >
            {shape.label}
          </text>
        </svg>
      ) : (
        <text
          x={textX}
          y={textY}
          transform={`rotate(${textRotation} ${textX} ${layout.text.y})`}
          style={textStyle}
          textAnchor={textAnchor}
          dominantBaseline="middle"
        >
          {shape.label}
        </text>
      )}
      <g
        transform={
          shape.rotation
            ? `rotate(${-shape.rotation}, ${layout.circle.x}, ${layout.circle.y})`
            : undefined
        }
      >
        {isViewMode ? (
          <InteractiveZoneSVG
            interactiveZone={interactiveZone}
            shape={shape}
            layout={layout}
            isActive={!shape.isInactive}
            isSpotHovered={isSpotHovered}
          />
        ) : (
          <>
            {/* "Show dot" off: the area is the indicator on the map, so the
                editor leaves the dot out too; the small marker still says
                where it is. */}
            {shape.stateArea?.hideDot ? null : (
              <circle
                cx={dot.x}
                cy={dot.y}
                r={circleRadius}
                fill={
                  shape.isInactive
                    ? shape.rectProps?.color || DEFAULT_NODE_RECT_COLOR
                    : dotStyle.fill
                }
                stroke={dotStyle.stroke}
                strokeWidth={circleStrokeWidth}
                strokeDasharray={dotStyle.dash?.join(" ")}
              />
            )}
            <circle
              cx={dot.x}
              cy={dot.y}
              r={5}
              fill={shape.resource_id ? "#60bb10" : "orange"}
              opacity={0.5}
            />
          </>
        )}
      </g>
    </g>
  );
};

interface StateAreaEditPreviewProps {
  shape: SpotShape;
  layout: ReturnType<typeof getOrientationLayout>;
  hasRect: boolean;
  cornerRadius: number;
}

const EDIT_OUTLINE_COLOR = "#2563eb";

/** Dashed outline of the configured highlight area, shown only while the
 *  spot is hovered or selected (CSS on the shape root), over the area
 *  drawn exactly as the map draws it. Its transparent fill makes the
 *  whole area part of the spot's hit region, so a click anywhere in it
 *  selects the spot even where the map would paint nothing. A polygon
 *  still being drawn shows as an open dashed line at all times. */
const StateAreaEditPreview: React.FC<StateAreaEditPreviewProps> = ({
  shape,
  layout,
  hasRect,
  cornerRadius
}) => {
  const area = shape.stateArea;
  if (!area) return null;
  if (area.kind === "polygon") {
    const points = area.points ?? [];
    if (points.length === 0) return null;
    const list = points.map((point) => `${point.x},${point.y}`).join(" ");
    return points.length >= 3 ? (
      <polygon
        data-state-area-preview
        className="state-area-preview"
        points={list}
        fill="transparent"
        stroke={EDIT_OUTLINE_COLOR}
        strokeWidth={1.5}
        strokeDasharray="6 4"
        vectorEffect="non-scaling-stroke"
        opacity={0}
      />
    ) : (
      <polyline
        data-state-area-preview
        points={list}
        fill="none"
        stroke={EDIT_OUTLINE_COLOR}
        strokeWidth={1.5}
        strokeDasharray="6 4"
        vectorEffect="non-scaling-stroke"
      />
    );
  }
  const rect: {
    x: number;
    y: number;
    width: number;
    height: number;
    cornerRadius?: number;
    rotation?: number;
  } | null =
    area.kind === "spot-rect"
      ? hasRect && layout.rect.width && layout.rect.height
        ? {
            x: layout.rect.x,
            y: layout.rect.y,
            width: layout.rect.width,
            height: layout.rect.height,
            cornerRadius,
            rotation: layout.rect.rotation
          }
        : null
      : area.kind === "rect" && area.rect?.width && area.rect?.height
        ? area.rect
        : null;
  if (!rect) return null;
  return (
    <rect
      data-state-area-preview
      className="state-area-preview"
      x={rect.x}
      y={rect.y}
      width={rect.width}
      height={rect.height}
      rx={rect.cornerRadius || 0}
      transform={
        rect.rotation
          ? `rotate(${rect.rotation} ${rect.x} ${rect.y})`
          : undefined
      }
      fill="transparent"
      stroke={EDIT_OUTLINE_COLOR}
      strokeWidth={1.5}
      strokeDasharray="6 4"
      vectorEffect="non-scaling-stroke"
      opacity={0}
    />
  );
};
